import React, { useEffect, useRef, useState } from 'react';

// 섹션 1-2: Hero Section과 Battle Section을 원본 HTML과 완전히 동일하게 구현
const Section1and2: React.FC = () => {
  // Battle 섹션을 위한 상태와 ref
  const [isRunning, setIsRunning] = useState(false);
  const [shouldStop, setShouldStop] = useState(false); // 김대리 애니메이션 중지 플래그
  const battleSectionRef = useRef<HTMLElement>(null);
  const kimAnimationRef = useRef<number | null>(null); // 김대리 애니메이션 프레임 참조

  // 배틀 시스템 데이터 - 콘엑스는 5초에 완료되도록 수정
  const kimTimes = [600, 900, 1800, 1200, 600, 900]; // 기존 방식 시간 (초) - 그대로 유지
  const conexsTimes = [0.8, 0.8, 0.8, 0.8, 0.8, 1]; // 콘엑스 시간 (초) - 총 5초로 단축
  
  const kimTaskNames = [
    "프로젝트 분석 중...",
    "표준품셈 검색 중...",
    "엑셀 작업 중...",
    "검토 및 수정 중...",
    "문서 정리 중...",
    "최종 확인 중..."
  ];
  
  const kimTaskCompletedNames = [
    "프로젝트 분석 완료!",
    "표준품셈 검색 완료!",
    "엑셀 작업 완료!",
    "검토 및 수정 완료!",
    "문서 정리 완료!",
    "최종 확인 완료!"
  ];
  
  const conexsTaskNames = [
    "프로젝트 생성 중...",
    "품명 입력 중...",
    "수량 입력 중...",
    "자동 계산 중...",
    "자동 검증 중...",
    "문서 생성 중..."
  ];
  
  const conexsTaskCompletedNames = [
    "프로젝트 생성 완료!",
    "품명 입력 완료!",
    "수량 입력 완료!",
    "자동 계산 완료!",
    "자동 검증 완료!",
    "문서 생성 완료!"
  ];

  // 스크롤 애니메이션 초기화
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.scroll-animate');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Battle 섹션 자동 시작
  useEffect(() => {
    let isInView = false;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isRunning) {
            isInView = true;
            // 처음 시작
            setTimeout(() => {
              startBattle();
            }, 1000);
          } else if (!entry.isIntersecting) {
            isInView = false;
            // 뷰포트를 벗어나면 애니메이션 중지 플래그 설정
            setShouldStop(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (battleSectionRef.current) {
      observer.observe(battleSectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [isRunning]); // isRunning 의존성 추가

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startBattle = () => {
    if (isRunning) return;
    
    setIsRunning(true);
    resetBattle();
    
    // Start both vertical task flows
    runVerticalTasks('kim', kimTimes, kimTaskNames);
    runVerticalTasks('conexs', conexsTimes, conexsTaskNames);
  };

  const resetBattle = () => {
    const kimStatus = document.getElementById('kimStatus');
    const conexsStatus = document.getElementById('conexsStatus');
    const kimProgressTitle = document.getElementById('kimProgressTitle');
    const conexsProgressTitle = document.getElementById('conexsProgressTitle');
    const kimTime = document.getElementById('kimTime');
    const conexsTime = document.getElementById('conexsTime');
    const kimProgress = document.getElementById('kimProgress');
    const conexsProgress = document.getElementById('conexsProgress');

    if (kimStatus) kimStatus.textContent = 'Ready';
    if (conexsStatus) conexsStatus.textContent = 'Ready';
    if (kimProgressTitle) kimProgressTitle.textContent = '대기중...';
    if (conexsProgressTitle) conexsProgressTitle.textContent = '대기중...';
    if (kimTime) kimTime.textContent = '00:00';
    if (conexsTime) conexsTime.textContent = '00:00';
    if (kimProgress) (kimProgress as HTMLElement).style.width = '0%';
    if (conexsProgress) (conexsProgress as HTMLElement).style.width = '0%';
  };

  const fullReset = () => {
    // 기본 리셋
    resetBattle();
    
    // 모든 태스크 아이콘 초기화
    const kimTaskIcons = document.querySelectorAll('#kimTasks .task-icon');
    const conexsTaskIcons = document.querySelectorAll('#conexsTasks .task-icon');
    
    kimTaskIcons.forEach(icon => {
      const iconElement = icon as HTMLElement;
      iconElement.style.background = '#e5e7eb';
      iconElement.style.color = '#9ca3af';
      iconElement.style.border = '2px solid #e5e7eb';
    });
    
    conexsTaskIcons.forEach(icon => {
      const iconElement = icon as HTMLElement;
      iconElement.style.background = '#e5e7eb';
      iconElement.style.color = '#9ca3af';
      iconElement.style.border = '2px solid #e5e7eb';
    });
    
    // 승패 클래스 제거 및 동적 스타일 초기화
    const kimSection = document.getElementById('kimSection');
    const conexsSection = document.getElementById('conexsSection');
    
    if (kimSection) {
      kimSection.classList.remove('defeated');
      // defeated 클래스로 인한 동적 스타일 초기화
      kimSection.style.opacity = '';
      kimSection.style.transform = '';
      kimSection.style.filter = '';
    }
    
    if (conexsSection) {
      conexsSection.classList.remove('victory');
      // victory 클래스로 인한 동적 스타일 초기화
      conexsSection.style.transform = '';
      conexsSection.style.boxShadow = '';
      
      // 왕관 제거
      const crown = conexsSection.querySelector('.crown-badge');
      if (crown) {
        crown.remove();
      }
      
      // ::before 가상 요소 효과도 클래스 제거로 자동 해제됨
    }
    
    // 플래그 리셋
    setShouldStop(false);
    
    // 김대리 애니메이션 참조 리셋
    if (kimAnimationRef.current) {
      cancelAnimationFrame(kimAnimationRef.current);
      kimAnimationRef.current = null;
    }
  };

  const runVerticalTasks = (type: string, times: number[], taskNames: string[]) => {
    const completedTaskNames = type === 'kim' ? kimTaskCompletedNames : conexsTaskCompletedNames;
    const statusElement = document.getElementById(`${type}Status`);
    const progressTitleElement = document.getElementById(`${type}ProgressTitle`);
    const timeElement = document.getElementById(`${type}Time`);
    const progressElement = document.getElementById(`${type}Progress`);
    const taskItems = document.querySelectorAll(`#${type}Tasks .task-icon`);
    
    let currentTask = 0;
    let elapsedTime = 0;
    let taskStartTime: number | null = null;
    let animationId: number | null = null;
    
    if (statusElement) statusElement.textContent = 'Fighting';
    
    const processTask = () => {
      // 김대리 애니메이션 중지 체크
      if (type === 'kim' && shouldStop) {
        // 현재 진행 상태에서 멈춤
        if (animationId) cancelAnimationFrame(animationId);
        if (kimAnimationRef.current) {
          cancelAnimationFrame(kimAnimationRef.current);
          kimAnimationRef.current = null;
        }
        if (statusElement) statusElement.textContent = '중단됨';
        if (progressTitleElement) progressTitleElement.textContent = '📊 작업 중단...';
        return;
      }
      
      if (currentTask >= times.length) {
        if (statusElement) statusElement.textContent = type === 'conexs' ? 'Winner!' : 'Defeated';
        if (progressTitleElement) progressTitleElement.textContent = '완료!';
        
        if (type === 'conexs') {
          // 콘엑스가 완료되면 김대리 애니메이션 중지
          setShouldStop(true);
          // 김대리 애니메이션 프레임 즉시 취소
          if (kimAnimationRef.current) {
            cancelAnimationFrame(kimAnimationRef.current);
            kimAnimationRef.current = null;
          }
          addVictoryEffects();
          stopKimAnimation();
          finishBattle();
        }
        return;
      }
      
      if (progressTitleElement) progressTitleElement.textContent = taskNames[currentTask];
      
      if (taskItems[currentTask]) {
        const taskIcon = taskItems[currentTask] as HTMLElement;
        taskIcon.style.background = type === 'kim' ? '#EF4444' : '#2563eb';
        taskIcon.style.color = 'white';
        taskIcon.style.border = `2px solid ${type === 'kim' ? '#EF4444' : '#2563eb'}`;
      }
      
      // 콘엑스는 실제 시간대로, 김대리는 10배 가속
      const taskDuration = type === 'conexs' ? times[currentTask] * 1000 : times[currentTask] * 10;
      taskStartTime = Date.now();
      
      const updateProgress = () => {
        if (!taskStartTime) return;
        
        // 김대리 애니메이션 중지 체크
        if (type === 'kim' && shouldStop) {
          if (animationId) cancelAnimationFrame(animationId);
          if (statusElement) statusElement.textContent = '중단됨';
          if (progressTitleElement) progressTitleElement.textContent = '📊 작업 중단...';
          return;
        }
        
        const now = Date.now();
        const taskElapsed = now - taskStartTime;
        const taskProgress = Math.min(taskElapsed / taskDuration, 1);
        
        elapsedTime += 10;
        const totalProgress = ((currentTask + taskProgress) / times.length) * 100;
        
        if (progressElement) (progressElement as HTMLElement).style.width = totalProgress + '%';
        if (timeElement) timeElement.textContent = formatTime(Math.floor(elapsedTime / 1000));
        
        if (taskProgress < 1) {
          animationId = requestAnimationFrame(updateProgress);
          if (type === 'kim') kimAnimationRef.current = animationId;
        } else {
          // 작업 완료 시 아이콘을 초록색으로 변경
          if (taskItems[currentTask]) {
            const taskIcon = taskItems[currentTask] as HTMLElement;
            taskIcon.style.background = '#10B981';
            taskIcon.style.border = '2px solid #10B981';
          }
          
          // 작업 완료 시 라벨을 "완료!"로 변경
          if (progressTitleElement) {
            progressTitleElement.textContent = completedTaskNames[currentTask];
          }
          
          // 0.5초 동안 완료 메시지 표시 후 다음 태스크로
          setTimeout(() => {
            currentTask++;
            // 김대리 애니메이션이 중지되지 않은 경우에만 다음 태스크 진행
            if (type === 'kim' && shouldStop) {
              return; // 중지된 경우 더 이상 진행하지 않음
            }
            setTimeout(processTask, 100);
          }, 500);
        }
      };
      
      updateProgress();
    };
    
    processTask();
  };

  const addVictoryEffects = () => {
    const conexsSection = document.getElementById('conexsSection');
    if (conexsSection) {
      conexsSection.classList.add('victory');
      
      const crown = document.createElement('div');
      crown.className = 'crown-badge';
      crown.textContent = '👑';
      crown.style.cssText = `
        position: absolute;
        top: -8px;
        right: -8px;
        width: 40px;
        height: 40px;
        background: linear-gradient(135deg, #fbbf24, #f59e0b);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        box-shadow: 0 4px 15px rgba(245, 158, 11, 0.4);
        opacity: 0;
        transform: scale(0) rotate(-180deg);
        animation: crownAppear 1s ease-out 0.5s forwards;
      `;
      
      if (!conexsSection.querySelector('.crown-badge')) {
        conexsSection.style.position = 'relative';
        conexsSection.appendChild(crown);
      }
    }
    
    const kimSection = document.getElementById('kimSection');
    if (kimSection) {
      kimSection.classList.add('defeated');
    }
  };

  const stopKimAnimation = () => {
    const kimSection = document.getElementById('kimSection');
    const kimStatus = document.getElementById('kimStatus');
    
    if (kimSection) kimSection.classList.add('defeated');
    if (kimStatus) kimStatus.textContent = 'Defeated';
  };

  const finishBattle = () => {
    // 완료 메시지 표시
    const kimStatus = document.getElementById('kimStatus');
    const conexsStatus = document.getElementById('conexsStatus');
    
    if (kimStatus) kimStatus.textContent = '처리 시간: 3일';
    if (conexsStatus) conexsStatus.textContent = '✨ 5초 만에 완료!';
    
    // 5초 후 초기화하고 재시작
    setTimeout(() => {
      fullReset();
      setTimeout(() => {
        startBattle();
      }, 100);
    }, 5000);
  };

  return (
    <>
      {/* 1. 메인 히어로 섹션 */}
      <section className="section hero-section" id="hero">
        <div className="section-container">
          <div className="main-layout">
            <div className="hero-content scroll-animate">
              <div className="status-badge">
                <span>🏗️</span>
                <span>AI POWERED VERIFICATION</span>
              </div>
              
              <h1 className="hero-title">
                건설 견적 작업을<br/>
                <span className="title-highlight">AI로 자동화</span>하는<br/>
                혁신적인 솔루션
              </h1>
              
              <p className="hero-description">
                일위대가 검증부터 표준품셈 대조까지, AI가 복잡한 건설 견적 업무를 
                정확하고 빠르게 처리합니다. 견적 정확도 99%의 혁신을 경험하세요.
              </p>
              
              <a href="#demo" className="cta-button">
                <span>무료 체험하기</span>
                <span>→</span>
              </a>
              
              <div className="stats-row">
                <div className="stat-item">
                  <span className="stat-number">5K+</span>
                  <span className="stat-label">건설 프로젝트</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">99%</span>
                  <span className="stat-label">검증 정확도</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">95%</span>
                  <span className="stat-label">시간 단축</span>
                </div>
              </div>
            </div>

            <div className="timeline-section scroll-animate">
              <div className="process-timeline">
                <div className="timeline-line"></div>
                
                <div className="timeline-step">
                  <div className="timeline-dot"></div>
                  <div className="step-card">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                      <div style={{fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px'}}>기존 방식</div>
                      <div style={{fontSize: '12px', color: '#2563eb', fontWeight: '600', background: '#eff6ff', padding: '4px 12px', borderRadius: '12px'}}>3일</div>
                    </div>
                    <div style={{fontSize: 'clamp(16px, 2vw, 20px)', fontWeight: '700', color: '#1e293b', marginBottom: '8px'}}>수동 견적 작성</div>
                    <div style={{fontSize: 'clamp(14px, 1.5vw, 16px)', color: '#64748b', lineHeight: '1.5'}}>
                      복잡한 일위대가 계산과 표준품셈 대조를 수작업으로 처리
                    </div>
                  </div>
                </div>

                <div className="timeline-step">
                  <div className="timeline-dot"></div>
                  <div className="step-card">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                      <div style={{fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px'}}>1단계</div>
                      <div style={{fontSize: '12px', color: '#2563eb', fontWeight: '600', background: '#eff6ff', padding: '4px 12px', borderRadius: '12px'}}>1분</div>
                    </div>
                    <div style={{fontSize: 'clamp(16px, 2vw, 20px)', fontWeight: '700', color: '#1e293b', marginBottom: '8px'}}>일위대가 업로드</div>
                    <div style={{fontSize: 'clamp(14px, 1.5vw, 16px)', color: '#64748b', lineHeight: '1.5'}}>
                      AI가 일위대가 구조를 분석하며 품셈 코드를 자동 인식
                    </div>
                  </div>
                </div>

                <div className="timeline-step">
                  <div className="timeline-dot"></div>
                  <div className="step-card">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                      <div style={{fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px'}}>2단계</div>
                      <div style={{fontSize: '12px', color: '#2563eb', fontWeight: '600', background: '#eff6ff', padding: '4px 12px', borderRadius: '12px'}}>2분</div>
                    </div>
                    <div style={{fontSize: 'clamp(16px, 2vw, 20px)', fontWeight: '700', color: '#1e293b', marginBottom: '8px'}}>AI 검증 처리</div>
                    <div style={{fontSize: 'clamp(14px, 1.5vw, 16px)', color: '#64748b', lineHeight: '1.5'}}>
                      표준품셈과 대조하여 정확성과 적정성을 자동 검증
                    </div>
                  </div>
                </div>

                <div className="timeline-step">
                  <div className="timeline-dot"></div>
                  <div className="step-card">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                      <div style={{fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px'}}>완료!</div>
                      <div style={{fontSize: '12px', color: '#10b981', fontWeight: '600', background: '#f0fdf4', padding: '4px 12px', borderRadius: '12px'}}>3분</div>
                    </div>
                    <div style={{fontSize: 'clamp(16px, 2vw, 20px)', fontWeight: '700', color: '#1e293b', marginBottom: '8px'}}>견적서 완성</div>
                    <div style={{fontSize: 'clamp(14px, 1.5vw, 16px)', color: '#64748b', lineHeight: '1.5'}}>
                      정확도 99%의 견적서와 상세 검증 리포트 제공
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Battle 비교 섹션 */}
      <section className="section battle-section" id="battle" ref={battleSectionRef}>
        <div className="section-container scroll-animate">
          <div style={{textAlign: 'center', marginBottom: '60px'}}>
            <div className="status-badge">
              <span>⚡</span>
              <span>VERTICAL TASK FLOW</span>
            </div>
            <h1 style={{
              fontSize: 'clamp(32px, 5vw, 48px)', 
              fontWeight: '900', 
              marginBottom: '16px', 
              background: 'linear-gradient(135deg, #2563eb, #3b82f6)', 
              WebkitBackgroundClip: 'text', 
              WebkitTextFillColor: 'transparent', 
              backgroundClip: 'text'
            }}>
              기존작업방식 vs 콘엑스서비스
            </h1>
            <p style={{fontSize: 'clamp(16px, 2vw, 20px)', color: '#64748b'}}>
              세로형 투두리스트로 확인하는 단계별 진행과정
            </p>
          </div>
          
          <div className="battle-arena">
            {/* 기존 방식 섹션 */}
            <div className="competitor-section kim-section" id="kimSection">
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                  <div style={{position: 'relative'}}>
                    <div style={{
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '16px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '24px', 
                      fontWeight: 'bold', 
                      background: 'linear-gradient(135deg, #EF4444, #ff6b6b)', 
                      color: 'white'
                    }}>📊</div>
                  </div>
                  <div>
                    <h3 style={{fontSize: '20px', fontWeight: '800', marginBottom: '4px'}}>기존 작업방식</h3>
                    <p style={{fontSize: '13px', color: '#64748b'}}>엑셀 기반 전통적 방식</p>
                  </div>
                </div>
                <div style={{
                  padding: '6px 12px', 
                  borderRadius: '16px', 
                  fontSize: '11px', 
                  fontWeight: '600', 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px', 
                  background: '#f3f4f6', 
                  color: '#6b7280'
                }} id="kimStatus">Ready</div>
              </div>

              <div style={{
                margin: '20px 0', 
                padding: '16px', 
                background: 'rgba(239, 68, 68, 0.05)', 
                borderRadius: '12px', 
                border: '1px solid rgba(239, 68, 68, 0.1)'
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                  <div style={{fontSize: '14px', fontWeight: '600', color: '#0f172a'}} id="kimProgressTitle">대기중...</div>
                  <div style={{fontSize: '12px', color: '#EF4444', fontFamily: 'SF Mono, monospace', fontWeight: '600'}} id="kimTime">00:00</div>
                </div>
                <div style={{
                  width: '100%', 
                  height: '6px', 
                  background: '#f1f5f9', 
                  borderRadius: '3px', 
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%', 
                    background: 'linear-gradient(90deg, #EF4444, #dc2626)', 
                    borderRadius: '3px', 
                    width: '0%', 
                    transition: 'width 0.3s ease'
                  }} id="kimProgress"></div>
                </div>
              </div>

              <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}} id="kimTasks">
                {kimTaskNames.map((task, index) => (
                  <div key={index} style={{
                    padding: '12px 16px', 
                    background: '#f8fafc', 
                    borderRadius: '8px', 
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                      <div className="task-icon" style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        background: '#e5e7eb',
                        color: '#9ca3af',
                        border: '2px solid #e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease'
                      }}>{index + 1}</div>
                      <span style={{fontSize: '14px', fontWeight: '500', color: '#374151'}}>{task}</span>
                    </div>
                    <span style={{fontSize: '12px', color: '#6b7280'}}>{Math.floor(kimTimes[index] / 60)}분</span>
                  </div>
                ))}
              </div>

            </div>

            {/* 콘엑스 방식 섹션 */}
            <div className="competitor-section conexs-section" id="conexsSection">
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                  <div style={{position: 'relative'}}>
                    <div style={{
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '16px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '24px', 
                      fontWeight: 'bold', 
                      background: 'linear-gradient(135deg, #2563eb, #3b82f6)', 
                      color: 'white'
                    }}>🤖</div>
                  </div>
                  <div>
                    <h3 style={{fontSize: '20px', fontWeight: '800', marginBottom: '4px'}}>콘엑스 AI</h3>
                    <p style={{fontSize: '13px', color: '#64748b'}}>AI 기반 자동화 방식</p>
                  </div>
                </div>
                <div style={{
                  padding: '6px 12px', 
                  borderRadius: '16px', 
                  fontSize: '11px', 
                  fontWeight: '600', 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px', 
                  background: '#f3f4f6', 
                  color: '#6b7280'
                }} id="conexsStatus">Ready</div>
              </div>

              <div style={{
                margin: '20px 0', 
                padding: '16px', 
                background: 'rgba(37, 99, 235, 0.05)', 
                borderRadius: '12px', 
                border: '1px solid rgba(37, 99, 235, 0.1)'
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                  <div style={{fontSize: '14px', fontWeight: '600', color: '#0f172a'}} id="conexsProgressTitle">대기중...</div>
                  <div style={{fontSize: '12px', color: '#2563eb', fontFamily: 'SF Mono, monospace', fontWeight: '600'}} id="conexsTime">00:00</div>
                </div>
                <div style={{
                  width: '100%', 
                  height: '6px', 
                  background: '#f1f5f9', 
                  borderRadius: '3px', 
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%', 
                    background: 'linear-gradient(90deg, #2563eb, #1d4ed8)', 
                    borderRadius: '3px', 
                    width: '0%', 
                    transition: 'width 0.3s ease'
                  }} id="conexsProgress"></div>
                </div>
              </div>

              <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}} id="conexsTasks">
                {conexsTaskNames.map((task, index) => (
                  <div key={index} style={{
                    padding: '12px 16px', 
                    background: '#f8fafc', 
                    borderRadius: '8px', 
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                      <div className="task-icon" style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        background: '#e5e7eb',
                        color: '#9ca3af',
                        border: '2px solid #e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease'
                      }}>{index + 1}</div>
                      <span style={{fontSize: '14px', fontWeight: '500', color: '#374151'}}>{task}</span>
                    </div>
                    <span style={{fontSize: '12px', color: '#6b7280'}}>{Math.floor(conexsTimes[index] / 60)}분</span>
                  </div>
                ))}
              </div>

            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default Section1and2;